import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { FiArrowDownCircle, FiArrowUpCircle, FiArrowUpRight, FiArrowDownRight, FiGift, FiSearch, FiRefreshCw } from "react-icons/fi";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import { useGlobalContext } from "../../../context/context";
import { getAccessToken } from "../../../utils/investmentApi";
import "./Transaction.css";

const filters = [
  { id: "all", label: "All activity" },
  { id: "deposit", label: "Deposits" },
  { id: "withdrawal", label: "Withdrawals" },
  { id: "adjustments", label: "Profit & bonus" },
  { id: "investments", label: "Investments" },
];

function readStoredUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || "{}"); }
  catch (error) { return {}; }
}

function formatAmount(item) {
  const amount = Number(item.amount) || 0;
  const sign = amount > 0 ? "+" : amount < 0 ? "−" : "";
  const code = String(item.currencyCode || "").toUpperCase();
  const value = Math.abs(amount);
  if (/^[A-Z]{3}$/.test(code)) {
    try {
      return sign + new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 2 }).format(value);
    } catch (error) { return sign + code + " " + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  }
  const symbol = String(item.currencySymbol || "");
  return sign + symbol + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isVisibleInFilter(item, filter) {
  if (filter === "all") return true;
  if (filter === "adjustments") return item.type === "profit" || item.type === "bonus";
  if (filter === "investments") return item.type === "investment_buy" || item.type === "investment_sell";
  return item.type === filter;
}

function getTransactionIcon(type) {
  if (type === "deposit") return <FiArrowDownCircle aria-hidden="true" />;
  if (type === "withdrawal") return <FiArrowUpCircle aria-hidden="true" />;
  if (type === "investment_buy") return <FiArrowDownRight aria-hidden="true" />;
  if (type === "investment_sell") return <FiArrowUpRight aria-hidden="true" />;
  return <FiGift aria-hidden="true" />;
}

function statusLabel(status) {
  if (status === "completed") return "Completed";
  if (status === "failed") return "Failed";
  return "Pending";
}

const Transaction = () => {
  const accessToken = getAccessToken();
  const { baseUrl } = useGlobalContext();
  const [profile] = useState(readStoredUser);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const userId = profile && profile._id;
    if (!userId) {
      setLoading(false);
      setError("Your account details could not be loaded. Sign in again and retry.");
      return undefined;
    }
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    axios.get(baseUrl + "transactions/" + encodeURIComponent(userId), {
      headers: { token: accessToken },
      signal: controller.signal,
    }).then((response) => {
      if (active) setTransactions(Array.isArray(response.data && response.data.transactions) ? response.data.transactions : []);
    }).catch((requestError) => {
      if (active && requestError.name !== "AbortError" && !axios.isCancel(requestError)) {
        setError(requestError.response && requestError.response.data && requestError.response.data.message || "Transaction history could not be loaded.");
      }
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; controller.abort(); };
  }, [baseUrl, profile && profile._id, retry, accessToken]);

  const filteredTransactions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return transactions.filter((item) => {
      if (!isVisibleInFilter(item, activeFilter)) return false;
      if (!query) return true;
      return [item.title, item.description, item.type, item.status, item.sourceId, item.currencyCode]
        .filter(Boolean).join(" ").toLowerCase().includes(query);
    });
  }, [transactions, activeFilter, search]);

  const completedCount = transactions.filter((item) => item.status === "completed").length;
  const pendingCount = transactions.filter((item) => item.status === "pending").length;
  const displayName = profile && (profile.firstName || profile.name) || "Your";

  return (
    <section className="tx-page">
      <div className="tx-topbar"><DashboardHeader pathname={displayName} /></div>
      <main className="tx-content">
        <header className="tx-heading">
          <div><span className="tx-eyebrow">ACCOUNT ACTIVITY</span><h1>Transactions</h1><p>Deposits, withdrawals, account adjustments, and investments in one history.</p></div>
        </header>
        <section className="tx-summary" aria-label="Transaction summary">
          <article className="tx-summary-card"><span>ALL ACTIVITY</span><strong>{transactions.length}</strong><small>Recorded transactions</small></article>
          <article className="tx-summary-card tx-summary-complete"><span>COMPLETED</span><strong>{completedCount}</strong><small>Successfully processed</small></article>
          <article className="tx-summary-card tx-summary-pending"><span>IN PROGRESS</span><strong>{pendingCount}</strong><small>Waiting for an update</small></article>
        </section>
        <section className="tx-toolbar" aria-label="Filter transactions">
          <label className="tx-search"><FiSearch aria-hidden="true" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search activity or reference" aria-label="Search transactions" /></label>
          <div className="tx-filters" role="group" aria-label="Transaction type">
            {filters.map((filter) => <button key={filter.id} type="button" className={activeFilter === filter.id ? "is-active" : ""} aria-pressed={activeFilter === filter.id} onClick={() => setActiveFilter(filter.id)}>{filter.label}</button>)}
          </div>
        </section>
        <section className="tx-activity" aria-label="Transaction history">
          <div className="tx-activity-heading"><div><span className="tx-eyebrow">LATEST FIRST</span><h2>{activeFilter === "all" ? "All activity" : filters.find((filter) => filter.id === activeFilter).label}</h2></div><span className="tx-count">{filteredTransactions.length} {filteredTransactions.length === 1 ? "record" : "records"}</span></div>
          {loading ? <div className="tx-state" role="status">Loading your transaction history…</div> : error ? <div className="tx-state tx-state-error" role="alert"><span>{error}</span><button type="button" onClick={() => setRetry((count) => count + 1)}><FiRefreshCw aria-hidden="true" /> Retry</button></div> : filteredTransactions.length === 0 ? <div className="tx-state tx-state-empty"><span className="tx-empty-icon"><FiRefreshCw aria-hidden="true" /></span><strong>{search ? "No matching transactions" : "No activity yet"}</strong><p>{search ? "Try another search or choose a different filter." : "Your deposits, withdrawals, profit or bonus updates, and investment trades will appear here."}</p></div> : (
            <ul className="tx-list">
              {filteredTransactions.map((item) => {
                const date = item.createdAt && moment(item.createdAt).isValid() ? moment(item.createdAt).format("MMM D, YYYY · h:mm A") : "Date unavailable";
                const status = statusLabel(item.status);
                const amountClass = Number(item.amount) < 0 ? "is-debit" : "is-credit";
                return <li className="tx-row" key={item.id}>
                  <span className={"tx-type-icon tx-icon-" + item.type}>{getTransactionIcon(item.type)}</span>
                  <div className="tx-row-main"><strong>{item.title}</strong><span>{item.description}</span><small>{date}{item.sourceId ? " · Ref " + item.sourceId.slice(-8).toUpperCase() : ""}</small></div>
                  <div className="tx-row-meta"><strong className={"tx-amount " + amountClass}>{formatAmount(item)}</strong><span className={"tx-status tx-status-" + item.status}>{status}</span></div>
                </li>;
              })}
            </ul>
          )}
        </section>
        <footer className="tx-footer"><span>ProfitGrowing</span><span>Statuses reflect the latest transaction record.</span></footer>
      </main>
    </section>
  );
};

export default Transaction;
